import cx from 'classnames';
import { canUseDOM } from 'exenv';
import { t } from 'i18next';
import moment from 'moment';
import { Component, ReactElement } from 'react';
import { DF_LAUNCH_DATE, RAID_METADATA } from '../../../../shared/enums';
import { TRaidSummary } from '../../../../shared/types/raidTypes';
import { TRealmResource } from '../../../../../../shared/types/utilityTypes';
import { namedRoutes, resolveRoutePath } from '../../bundles/routes';
import { TRaceGuildInfo, TRaceProgressTimelineInfo, TRegionSummary } from '../../libs/store';
import {
  dateOnly,
  factionClass,
  formatMsShort,
  naturalShortDate,
  plural,
  relativeDate
} from '../../../../../../shared/frontend/libs/utils';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';
import { Link } from '../../../../../../shared/frontend/components/link';
import CountdownTimer from '../../../../../../shared/frontend/components/presentational/countdownTimer';
import { StreamTooltipContent } from '../../../../../../shared/frontend/components/presentational/streamPreview';
import { ComplexTooltip, SimpleTooltip } from '../../../../../../shared/frontend/components/presentational/tooltip';
import { TWW_GLOBAL_LAUNCH_DATES, TWW_SEASON2_DATES } from '../../../../../../shared/gameConfig/wowretail/dates';

type TRaceProgressTimelineNodeProps = {
  raid: TRaidSummary;
  region: TRegionSummary;
  realm?: TRealmResource;
  difficulty: string;
  node: TRaceProgressTimelineInfo;
  total: number;
  align: 'top' | 'bottom';
  faction?: string;
  maxGuildsPerNode?: number;
};

function RaceProgressTimelineNode(props: TRaceProgressTimelineNodeProps) {
  const difficultyTerm = t(`common:difficulty.${props.difficulty}.abbreviation`);

  const progressBadge = (
    <span className="text-white">
      <b>
        {props.node.progress}/{props.total} {difficultyTerm}
      </b>
    </span>
  );

  let getPathFn: any = null;
  if (props.faction) {
    if (props.realm) {
      getPathFn = resolveRoutePath.bind(null, namedRoutes.ProgressFactionRealmRankings);
    } else {
      getPathFn = resolveRoutePath.bind(null, namedRoutes.ProgressFactionRankings);
    }
  } else {
    if (props.realm) {
      getPathFn = resolveRoutePath.bind(null, namedRoutes.ProgressRealmRankings);
    } else {
      getPathFn = resolveRoutePath.bind(null, namedRoutes.ProgressRankings);
    }
  }

  const visibleGuilds =
    props.maxGuildsPerNode && props.maxGuildsPerNode < 3
      ? props.node.guilds.slice(0, props.maxGuildsPerNode)
      : props.node.guilds;

  return (
    <div className={`rio-race-node rio-race-node--${props.align}`}>
      <div className="rio-race-node--node">
        <div className={cx({ 'rio-race-node--bg': true, 'rio-race-node--empty': props.node.totalGuilds === 0 })} />
        {/* This is disabled for now because there are z-index issues with
        overlapping nodes, preventing certain nodes from being highlighted */}

        {/*<SimpleTooltip placement="top" tooltip={
            <div className="slds-text-body--small slds-m-bottom--xx-small">
              Guilds at {progressBadge}
            </div>
          }>
          <div className="text-white rio-race-node--progress-text">
            {props.node.progress}/{props.total}
          </div>
        </SimpleTooltip>*/}
        <div className="text-white rio-race-node--progress-text">
          <Link
            className="rio-realm-link"
            to={getPathFn({
              raid: props.raid.slug,
              difficulty: props.difficulty,
              progress: props.node.progress,
              region: props.region.slug,
              faction: props.faction,
              realm: props.realm ? props.realm.slug || 'all' : undefined,
              page: 0
            })}
          >
            {props.node.progress}/{props.total}
          </Link>
        </div>
      </div>
      <div className="rio-race-node--guilds">
        {visibleGuilds.map((guildInfo: TRaceGuildInfo, index: number) => (
          <p key={index}>
            <ComplexTooltip
              placement="top"
              tooltip={
                <div>
                  <div className="slds-text-body--small">
                    {guildInfo.guild.region.slug !== 'cn' ? (
                      <>
                        <div className="slds-m-bottom--xx-small">Reached {progressBadge} On:</div>
                        <p>{naturalShortDate(guildInfo.defeatedAt.toString())}</p>
                        <p>({relativeDate(guildInfo.defeatedAt.toString())})</p>
                      </>
                    ) : (
                      <>
                        <div className="slds-m-bottom--xx-small">Reached {progressBadge} On:</div>
                        <p>{dateOnly(guildInfo.defeatedAt.toString())}</p>
                        <p>({relativeDate(guildInfo.defeatedAt.toString())})</p>
                      </>
                    )}
                  </div>
                  {guildInfo.streamers.count > 0 && (
                    <StreamTooltipContent
                      type="twitch"
                      stream={guildInfo.streamers.stream}
                      count={guildInfo.streamers.count}
                    />
                  )}
                </div>
              }
            >
              <span className={guildInfo.streamers.count > 0 ? 'streaming-now' : ''}>
                <Link
                  className={cx(
                    'rio-race-node--guild-text',
                    'slds-text-heading--small',
                    'rio-text--shadow',
                    'rio-text--thin',
                    factionClass(guildInfo.guild.faction)
                  )}
                  to={resolveRoutePath(namedRoutes.GuildDetails, {
                    region: guildInfo.guild.region.slug,
                    realm: guildInfo.guild.realm.slug,
                    guild: guildInfo.guild.name
                  })}
                >
                  {index + 1}. {guildInfo.guild.displayName}
                </Link>
              </span>
            </ComplexTooltip>
          </p>
        ))}
        <p
          className={cx(
            'rio-race-node--more-text',
            'slds-text-heading--small',
            'rio-text--shadow',
            'rio-text--thin',
            'text-white'
          )}
        >
          {props.node.totalGuilds > props.node.guilds.length && (
            <SimpleTooltip
              placement="top"
              tooltip={
                <div className="slds-text-body--small">
                  {props.node.totalGuilds - props.node.guilds.length} more {}{' '}
                  {plural('guild', props.node.totalGuilds - props.node.guilds.length)} at {} {progressBadge}
                </div>
              }
            >
              <span>
                <Link
                  className="rio-realm-link text-muted"
                  to={getPathFn({
                    raid: props.raid.slug,
                    difficulty: props.difficulty,
                    progress: props.node.progress,
                    region: props.region.slug,
                    faction: props.faction,
                    realm: props.realm ? props.realm.slug || 'all' : undefined,
                    page: 0
                  })}
                >
                  {props.node.totalGuilds - props.node.guilds.length} More{' '}
                  {plural('Guild', props.node.totalGuilds - props.node.guilds.length)}
                  <i className="fa fa-caret-right slds-m-left--xx-small" />
                </Link>
              </span>
            </SimpleTooltip>
          )}
        </p>
      </div>
    </div>
  );
}

export type TRaidProgressTimeline = {
  difficulty: string;
  timeline: TRaceProgressTimelineInfo[];
  raid: TRaidSummary;
  region: TRegionSummary;
  realm?: TRealmResource;
  faction?: string;
  maxGuildsPerNode?: number;
  overrideContainerHeight?: number;
};

function RaidProgressTimeline(props: TRaidProgressTimeline) {
  const containerHeight = props.overrideContainerHeight || 250;

  if (!props.timeline) {
    return <div style={{ height: containerHeight }} />;
  }

  const topNodes = props.timeline.filter((node, index) => index % 2 === 0);
  const bottomNodes = props.timeline.filter((node, index) => index % 2 === 1);

  return (
    <div style={{ height: containerHeight }} className={`rio-race-bosses--${props.timeline.length}`}>
      <div className="rio-race-row--top">
        {topNodes.map((node: TRaceProgressTimelineInfo, index: number) => (
          <RaceProgressTimelineNode
            difficulty={props.difficulty}
            key={node.progress}
            node={node}
            total={props.timeline.length}
            align="bottom"
            raid={props.raid}
            realm={props.realm}
            region={props.region}
            faction={props.faction}
            maxGuildsPerNode={props.maxGuildsPerNode}
          />
        ))}
      </div>
      <div className="rio-race-row--bottom">
        {bottomNodes.map((node: TRaceProgressTimelineInfo, index: number) => (
          <RaceProgressTimelineNode
            difficulty={props.difficulty}
            key={node.progress}
            node={node}
            total={props.timeline.length}
            align="top"
            raid={props.raid}
            realm={props.realm}
            region={props.region}
            faction={props.faction}
            maxGuildsPerNode={props.maxGuildsPerNode}
          />
        ))}
      </div>
    </div>
  );
}

export type TRaceProgressTimelineContainerProps = {
  region: TRegionSummary;
  difficulty: string;
  raid: TRaidSummary;
  timeline: TRaceProgressTimelineInfo[];
  realm: TRealmResource;
  isConnectedRealm: boolean | undefined;
  raidLockState: any;
  faction?: string;
  hidePepe?: boolean;
  hideBackground?: boolean;
  maxGuildsPerNode?: number;
  overrideContainerHeight?: number;
  children?: React.ReactNode;
};

/*
# Tuesday 15:00 UTC (10:00am central time) is reset for US
# Wednesday 7:00 UTC is reset for EU
# Wednesday 23:00 UTC is reset for KR
# Wednesday 23:00 UTC is reset for TW
*/

function getMythicDifficultyOpenWeek(ts: moment.Moment) {
  if (ts < DF_LAUNCH_DATE || (ts > TWW_GLOBAL_LAUNCH_DATES.us && ts < TWW_SEASON2_DATES.us)) {
    return 1;
  }
  return 0;
}

function getTimestampForRaidDifficulty(timestamp: any, difficulty: string) {
  const ts = moment.utc(timestamp);
  if (difficulty === 'mythic') {
    const numWeeks = getMythicDifficultyOpenWeek(ts);
    if (numWeeks > 0) {
      return ts.add(numWeeks, 'week');
    }
  }
  return ts;
}

export function getRaidLockState(region: string, raid: string, difficulty: string): any {
  const raidUnlockDates: any = RAID_METADATA.find((r) => r.slug === raid);
  if (raidUnlockDates && raidUnlockDates.starts[region]) {
    const timestamp = getTimestampForRaidDifficulty(raidUnlockDates.starts[region], difficulty);
    if (!timestamp) {
      return {
        locked: false
      };
    }
    const diff = timestamp.diff(moment.utc());
    return {
      locked: diff > 0,
      timestamp
    };
  } else {
    return {
      locked: false
    };
  }
}

export default class RaceProgressTimelineContainer extends Component<TRaceProgressTimelineContainerProps, any> {
  public render(): ReactElement<TRaceProgressTimelineContainerProps> {
    return (
      <div
        className="rio-background--header"
        style={
          this.props.hideBackground
            ? {}
            : {
                backgroundImage:
                  'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)),' +
                  `url(${asset(`/images/${this.props.raid.slug}/headers/_zone.jpg`)})`,
                position: 'relative',
                backgroundPosition: 'center top'
              }
        }
      >
        {this.props.raidLockState.locked ? (
          <div style={{ height: 250 }} className="slds-p-around--medium">
            <div className="slds-grid slds-grid--vertical-align-center rio-full-height">
              <div className="slds-col rio-text--title slds-text-align--center">
                {this.props.children}
                <div className="slds-m-top--large slds-text-heading--medium">
                  Unlocks In{' '}
                  <CountdownTimer
                    style={{ display: 'inline-block' }}
                    to={this.props.raidLockState.timestamp}
                    formatter={formatMsShort}
                    onComplete={
                      canUseDOM
                        ? window.location.reload.bind(window.location)
                        : () => {
                            /* nop */
                          }
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="slds-p-horizontal--medium slds-p-vertical--small">
            {this.props.children}
            <div className="rio-race-container slds-m-top--small">
              <div className="rio-race-line" />
              <div className="rio-race-bosses-container">
                <RaidProgressTimeline
                  difficulty={this.props.difficulty}
                  timeline={this.props.timeline}
                  raid={this.props.raid}
                  region={this.props.region}
                  realm={this.props.realm}
                  faction={this.props.faction}
                  maxGuildsPerNode={this.props.maxGuildsPerNode}
                  overrideContainerHeight={this.props.overrideContainerHeight}
                />
              </div>
            </div>
          </div>
        )}

        {!this.props.hidePepe && (
          <PepeHighlight
            show={
              this.props.timeline?.length > 0 && this.props.timeline[this.props.timeline.length - 1].totalGuilds === 0
            }
          />
        )}
      </div>
    );
  }
}

export function PepeHighlight(props: any) {
  if (props.show) {
    return <Link to="/discord" className="rio-hidden--small highlight-feature highlight-feature--pepe" />;
  } else {
    return null;
  }
}
